import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { Reveal } from "../components/Reveal";

const TYPES = ["Apartment", "Villa", "Penthouse", "Builder Floor", "Plot"];
const PRICE_BANDS = [
  { label: "Any Price", min: "", max: "" },
  { label: "Under ₹1 Cr", min: "", max: 10000000 },
  { label: "₹1 – 5 Cr", min: 10000000, max: 50000000 },
  { label: "₹5 – 15 Cr", min: 50000000, max: 150000000 },
  { label: "Above ₹15 Cr", min: 150000000, max: "" },
];

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = useMemo(() => ({
    listing_type: params.get("listing_type") || "all",
    type: params.get("type") || "all",
    beds: params.get("beds") || "",
    locality: params.get("locality") || "all",
    price: params.get("price") || "0",
    sort: params.get("sort") || "newest",
  }), [params]);

  useEffect(() => {
    api.get("/properties/localities").then(({ data }) => setLocalities(data.localities)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const band = PRICE_BANDS[Number(filters.price)] || PRICE_BANDS[0];
    const query = {
      listing_type: filters.listing_type,
      type: filters.type,
      locality: filters.locality,
      sort: filters.sort,
      q: q || undefined,
      beds: filters.beds || undefined,
      min_price: band.min || undefined,
      max_price: band.max || undefined,
    };
    const t = setTimeout(() => {
      api.get("/properties", { params: query })
        .then(({ data }) => setProperties(data.properties))
        .catch(() => setProperties([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filters, q]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all" && value !== "0") next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const resetFilters = () => {
    setQ("");
    setParams({}, { replace: true });
  };

  const hasActiveFilters = q || ["listing_type", "type", "locality", "beds", "price", "sort"].some((k) => params.get(k));

  const selectCls = "border border-white/15 bg-ink px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-smoke outline-none transition-colors focus:border-gold";

  return (
    <div className="min-h-screen pt-28" data-testid="listings-page">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">The Collection</p>
          <h1 className="mt-3 font-serif text-5xl font-light text-bone sm:text-6xl">Residences</h1>
        </Reveal>

        <div className="sticky top-[68px] z-30 mt-10 border border-white/10 bg-black/85 backdrop-blur-2xl" data-testid="filter-bar">
          <button
            className="flex w-full items-center justify-between px-4 py-3 lg:hidden"
            data-testid="filters-toggle"
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-expanded={filtersOpen}
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
              {filtersOpen ? "Hide search & filters" : "Search & filters"}
            </span>
            <SlidersHorizontal size={15} className="text-gold" />
          </button>
          <div className={`${filtersOpen ? "flex" : "hidden"} flex-wrap items-center gap-3 px-4 pb-4 lg:flex lg:p-4`} data-testid="filter-controls">
            <div className="flex min-w-[220px] flex-1 items-center gap-2 border border-white/15 px-3">
              <Search size={14} className="text-gold" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, locality, project…"
                data-testid="filter-search-input"
                className="w-full bg-transparent py-2.5 text-sm font-light text-bone outline-none placeholder:text-smoke/60"
              />
            </div>
            <select value={filters.listing_type} onChange={(e) => setParam("listing_type", e.target.value)} className={selectCls} data-testid="filter-listing-type">
              <option value="all">Buy + Lease</option>
              <option value="sale">For Sale</option>
              <option value="rent">For Lease</option>
            </select>
            <select value={filters.type} onChange={(e) => setParam("type", e.target.value)} className={selectCls} data-testid="filter-property-type">
              <option value="all">All Types</option>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filters.locality} onChange={(e) => setParam("locality", e.target.value)} className={selectCls} data-testid="filter-locality">
              <option value="all">All Localities</option>
              {localities.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select value={filters.beds} onChange={(e) => setParam("beds", e.target.value)} className={selectCls} data-testid="filter-beds">
              <option value="">Any Beds</option>
              {[1, 2, 3, 4, 5].map((b) => <option key={b} value={b}>{b}+ Beds</option>)}
            </select>
            <select value={filters.price} onChange={(e) => setParam("price", e.target.value)} className={selectCls} data-testid="filter-price">
              {PRICE_BANDS.map((b, i) => <option key={b.label} value={i}>{b.label}</option>)}
            </select>
            <select value={filters.sort} onChange={(e) => setParam("sort", e.target.value)} className={selectCls} data-testid="filter-sort">
              <option value="newest">Newest</option>
              <option value="price_asc">Price ↑</option>
              <option value="price_desc">Price ↓</option>
            </select>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                data-testid="filter-reset"
                className="border border-gold/40 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-ink"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke" data-testid="results-count">
          {loading ? "Curating…" : `${properties.length} residence${properties.length === 1 ? "" : "s"}`}
        </p>

        <div className="mt-6 grid grid-cols-1 gap-8 pb-32 sm:grid-cols-2 xl:grid-cols-3" data-testid="listings-grid">
          {properties.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i, 5) * 0.06}>
              <PropertyCard property={p} index={i} />
            </Reveal>
          ))}
          {!loading && properties.length === 0 && (
            <div className="col-span-full border border-white/10 p-20 text-center" data-testid="empty-results">
              <p className="font-serif text-3xl text-bone">Nothing matches — yet.</p>
              <p className="mt-3 text-sm font-light text-smoke">Loosen a filter, or tell our desk what you're hunting for.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
